import { ReactComponent as AreaChartIcon } from 'assets/icons/a11ymenu_area_chart.svg';
import { ReactComponent as BarChartIcon } from 'assets/icons/a11ymenu_bar_chart.svg';
import { ReactComponent as ColumnChartIcon } from 'assets/icons/a11ymenu_column_chart.svg';
import { ReactComponent as ComboChartIcon } from 'assets/icons/a11ymenu_combo_chart.svg';
import { ReactComponent as DonutChartIcon } from 'assets/icons/a11ymenu_donut_chart.svg';
import { ReactComponent as FunnelChartIcon } from 'assets/icons/a11ymenu_funnel_chart.svg';
import { ReactComponent as HalfDonutChartIcon } from 'assets/icons/a11ymenu_halfdonut_chart.svg';
import { ReactComponent as HalfPieChartIcon } from 'assets/icons/a11ymenu_halfpie_chart.svg';
import { ReactComponent as LineChartIcon } from 'assets/icons/a11ymenu_line_chart.svg';
import { ReactComponent as PieChartIcon } from 'assets/icons/a11ymenu_pie_chart.svg';
import { ReactComponent as ScatterBubbleChartIcon } from 'assets/icons/a11ymenu_scatter_bubble_chart.svg';
import { ReactComponent as StackedBarChartIcon } from 'assets/icons/a11ymenu_stackedbar_chart.svg';
import { ReactComponent as StackedColumnChartIcon } from 'assets/icons/a11ymenu_stackedcolumn_chart.svg';
import { ReactComponent as WordCloudChartIcon } from 'assets/icons/a11ymenu_wordcloud_chart.svg';
import { ReactComponent as CrownIcon } from 'assets/icons/crown_upgrade_fill.svg';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CHART_EDITED, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import clonedeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import {
  CHART_TYPE_TO_DEFAULT_DATA_MAP,
  ChartType,
} from 'modules/Editor/components/WidgetMenu/SideMenu/ChartsWidgetMenu/ChartsWidgetMenu.config';
import { RootState, store } from 'modules/Editor/store';
import { UserType, UserTypes, hasResourcePermission, useUser } from 'modules/User';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import {
  DropdownPopoverOptions,
  generateTranslatedOptions,
} from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { ReactElement, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicWidgetTypes, WidgetType } from 'types/widget.types';
import { isFiniteNumber } from 'utils/number';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { useWidget } from 'widgets/sdk';

import { appendControlData, getChartInfo } from '../ChartWidget.helpers';

type DataTableChartType =
  | typeof ChartType.Area
  | typeof ChartType.Bar
  | typeof ChartType.Column
  | typeof ChartType.Combo
  | typeof ChartType.DonutPie
  | typeof ChartType.HalfDonutPie
  | typeof ChartType.HalfPie
  | typeof ChartType.Line
  | typeof ChartType.Pie
  | typeof ChartType.StackedColumn
  | typeof ChartType.StackedBar
  | typeof ChartType.StackedArea
  | typeof ChartType.Funnel
  | typeof ChartType.ScatterBubble
  | typeof ChartType.WordCloud;

type ChartOptionType = {
  value: DataTableChartType;
  label: string;
  icon: ReactElement;
  permission?: UserType;
  widgetType: WidgetType;
};

type AvailableChartsType = {
  [key in DataTableChartType]: ChartOptionType;
};

const AVAILABLE_CHARTS: AvailableChartsType = {
  [ChartType.Pie]: {
    value: ChartType.Pie,
    widgetType: WidgetType.PieChart,
    label: 'options.pie',
    icon: <PieChartIcon width='15' />,
  },
  [ChartType.DonutPie]: {
    value: ChartType.DonutPie,
    widgetType: WidgetType.PieChart,
    label: 'options.donut',
    icon: <DonutChartIcon width='15' />,
  },
  [ChartType.Column]: {
    value: ChartType.Column,
    widgetType: WidgetType.ColumnChart,
    label: 'options.column',
    icon: <ColumnChartIcon width='15' />,
  },
  [ChartType.Combo]: {
    value: ChartType.Combo,
    widgetType: WidgetType.ComboChart,
    label: 'options.combo',
    icon: <ComboChartIcon width='15' />,
  },
  [ChartType.Bar]: {
    value: ChartType.Bar,
    widgetType: WidgetType.BarChart,
    label: 'options.bar',
    icon: <BarChartIcon width='15' />,
  },
  [ChartType.HalfDonutPie]: {
    value: ChartType.HalfDonutPie,
    widgetType: WidgetType.HalfPieChart,
    label: 'options.halfDonut',
    icon: <HalfDonutChartIcon width='15' />,
  },
  [ChartType.HalfPie]: {
    value: ChartType.HalfPie,
    widgetType: WidgetType.HalfPieChart,
    label: 'options.halfPie',
    icon: <HalfPieChartIcon width='15' />,
  },
  [ChartType.Line]: {
    value: ChartType.Line,
    widgetType: WidgetType.LineChart,
    label: 'options.line',
    icon: <LineChartIcon width='15' />,
  },
  [ChartType.Area]: {
    value: ChartType.Area,
    widgetType: WidgetType.AreaChart,
    label: 'options.area',
    icon: <AreaChartIcon width='15' />,
  },
  [ChartType.StackedColumn]: {
    value: ChartType.StackedColumn,
    widgetType: WidgetType.StackedColumnChart,
    label: 'options.stackedCol',
    icon: <StackedColumnChartIcon width='15' />,
  },
  [ChartType.StackedBar]: {
    value: ChartType.StackedBar,
    widgetType: WidgetType.StackedBarChart,
    label: 'options.stackedBar',
    icon: <StackedBarChartIcon width='15' />,
  },
  [ChartType.StackedArea]: {
    value: ChartType.StackedArea,
    widgetType: WidgetType.StackedAreaChart,
    label: 'options.stackedArea',
    icon: <AreaChartIcon width='15' />,
  },
  [ChartType.Funnel]: {
    value: ChartType.Funnel,
    widgetType: WidgetType.FunnelChart,
    label: 'options.funnel',
    icon: <FunnelChartIcon width='15' />,
  },
  [ChartType.ScatterBubble]: {
    value: ChartType.ScatterBubble,
    widgetType: WidgetType.ScatterBubbleChart,
    label: 'options.scatterBubble',
    icon: <ScatterBubbleChartIcon width='15' />,
  },
  [ChartType.WordCloud]: {
    value: ChartType.WordCloud,
    widgetType: WidgetType.WordCloudChart,
    label: 'options.wordCloud',
    permission: UserTypes.Premium,
    icon: <WordCloudChartIcon width='15' />,
  },
};

const SINGLE_SERIES_CHARTS: ChartOptionType[] = [
  AVAILABLE_CHARTS[ChartType.Pie],
  AVAILABLE_CHARTS[ChartType.DonutPie],
  AVAILABLE_CHARTS[ChartType.HalfPie],
  AVAILABLE_CHARTS[ChartType.HalfDonutPie],
  AVAILABLE_CHARTS[ChartType.Column],
  AVAILABLE_CHARTS[ChartType.Bar],
  AVAILABLE_CHARTS[ChartType.Line],
  AVAILABLE_CHARTS[ChartType.Area],
  AVAILABLE_CHARTS[ChartType.Funnel],
  AVAILABLE_CHARTS[ChartType.WordCloud],
];

const MULTI_SERIES_CHARTS: ChartOptionType[] = [
  AVAILABLE_CHARTS[ChartType.Column],
  AVAILABLE_CHARTS[ChartType.Combo],
  AVAILABLE_CHARTS[ChartType.Bar],
  AVAILABLE_CHARTS[ChartType.Line],
  AVAILABLE_CHARTS[ChartType.Area],
  AVAILABLE_CHARTS[ChartType.StackedBar],
  AVAILABLE_CHARTS[ChartType.StackedColumn],
  AVAILABLE_CHARTS[ChartType.StackedArea],
];

export const WidgetTypeToChartTypeMap = new Map(
  Object.entries(AVAILABLE_CHARTS).map<[WidgetType, ChartType]>(([key, value]) => {
    return [value.widgetType, key as ChartType];
  }),
);

export const mergeChartData = (targetChartType: ChartType, originChartData: ChartWidgetData) => {
  const targetWidgetDefaultData = clonedeep(CHART_TYPE_TO_DEFAULT_DATA_MAP[targetChartType]);
  const existingWidgetData = originChartData;

  targetWidgetDefaultData.widgetData = merge(targetWidgetDefaultData.widgetData, existingWidgetData);

  // Avoid _merge messing up data table for default values with longer data
  (targetWidgetDefaultData.widgetData as ChartWidgetData).seriesData = existingWidgetData.seriesData;
  (targetWidgetDefaultData.widgetData as ChartWidgetData).seriesMeta = existingWidgetData.seriesMeta;

  // If we are changing to Donut, then set innerSize to 50.
  if (
    (targetChartType === ChartType.DonutPie || targetChartType === ChartType.HalfDonutPie) &&
    (targetWidgetDefaultData.widgetData as ChartWidgetData).generalOptions.innerSize === 0
  ) {
    (targetWidgetDefaultData.widgetData as ChartWidgetData).generalOptions.innerSize = 50;
  }

  // If we are changing to Pie, then set innerSize to 0.
  if (
    (targetChartType === ChartType.Pie || targetChartType === ChartType.HalfPie) &&
    (targetWidgetDefaultData.widgetData as ChartWidgetData).generalOptions.innerSize > 0
  ) {
    (targetWidgetDefaultData.widgetData as ChartWidgetData).generalOptions.innerSize = 0;
  }

  if (targetChartType === ChartType.Combo) {
    (targetWidgetDefaultData.widgetData as ChartWidgetData).seriesData = appendControlData(
      existingWidgetData.seriesData,
    );
  }

  return targetWidgetDefaultData;
};

/**
 * Addthe permission data to the chart swapper deopdown options
 * Ex: Add add the hover state to the word cloud chart option if the user does not have the permission to access
 * @param {ChartOptionType} chartOption the options array used by the dropdown popover
 * @param {UserType} userType the user type, ex: free, premium, etc
 * @returns {ChartOptionType & DropdownPopoverOptions[number]} the chart option with the permission data
 */
export const appendPermissionData = (
  chartOption: ChartOptionType,
  userType: UserType,
  permissionLabel: string,
): ChartOptionType & DropdownPopoverOptions[number] => {
  const hasPermission = chartOption.permission ? hasResourcePermission(userType, chartOption.permission) : true;

  return {
    ...chartOption,
    hoveredOptionProps: !hasPermission
      ? {
          color: 'upgrade.blue.500',
          textDecoration: 'underline',
        }
      : undefined,
    hoveredlabel: !hasPermission ? permissionLabel : undefined,
    hoveredIcon: !hasPermission ? <CrownIcon color='var(--vg-colors-upgrade-blue-500)' /> : undefined,
  };
};

const ChartSwapper = () => {
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'chartSwap' });
  const label = t('label');
  const ariaLabel = t('chartSwapButtonAriaLabel');
  const permissionLabel: string = t('permission');
  const { trackChameleon } = useChameleon();

  const {
    widgetId,
    seriesData,
    generalOptions: { innerSize },
    replaceWidget,
  } = useWidget<ChartWidgetData>();
  const optionsRef = useRef<(ChartOptionType & DropdownPopoverOptions[number])[]>([]);

  const { userType } = useUser();

  const widgetType = getWidgetTypeFromId(widgetId) as BasicWidgetTypes;
  const isDonutPie = widgetType === WidgetType.PieChart && innerSize > 0;
  const isHalfDonutPie = widgetType === WidgetType.HalfPieChart && innerSize > 0;

  const isMultiSeries =
    seriesData
      .map(
        (serie, index) => (index === 0 ? null : serie.name), // Don't consider A column
      )
      .filter((name) => !!name).length > 1;

  let swapOptions = useMemo(() => {
    const chartOptions = isMultiSeries ? MULTI_SERIES_CHARTS : SINGLE_SERIES_CHARTS;
    return chartOptions.map((value) => appendPermissionData(value, userType, permissionLabel));
  }, [isMultiSeries, userType, permissionLabel]);

  const onSelect = useCallback(
    (targetChartName: string, idx: number) => {
      // In this component, when the hoverIcon exists, it means the user does not have the permission
      if (isFiniteNumber(idx) && optionsRef.current[idx]?.hoveredIcon) {
        Mixpanel.track(UPGRADE.eventName, {
          Feature: UPGRADE.feature.PremiumChart,
          chart_type: targetChartName,
          'User Plan': getUserPlanProperty(userType),
        });
        trackChameleon(UPGRADE.eventName);

        setAnalyticsUpgradeFeatureProp(UPGRADE.feature.PremiumChart, targetChartName);
        window.open(`${UPGRADE_REDIRECT_LINK}/${optionsRef.current[idx].permission}`, '_self');
        return;
      }

      const currentWidgetData = (store.getState() as RootState).infograph.widgets[widgetId] as ChartWidgetData;

      const targetChartType = targetChartName as unknown as ChartType;
      const previousWidget = getChartInfo(getWidgetTypeFromId(widgetId)).widgetTypeName;
      replaceWidget(mergeChartData(targetChartType, currentWidgetData));
      Mixpanel.track(CHART_EDITED.eventName, {
        styling_property: CHART_EDITED.styling_property.ChartSwap,
        styling_value: targetChartType,
        chart_type: previousWidget,
      });
    },
    [replaceWidget, userType, widgetId, trackChameleon],
  );

  // Keep the current widget type in the select even if we changed the data in the data table, and it
  // now becomes Single or Multi series, and it is not in that list.
  if (swapOptions.findIndex((option) => option.widgetType === widgetType) === -1) {
    const additionalOptions = Object.values(AVAILABLE_CHARTS).filter((option) => option.widgetType === widgetType);
    const additionalOptionsWithPermission = additionalOptions.map((value) =>
      appendPermissionData(value, userType, permissionLabel),
    );
    swapOptions = [...additionalOptionsWithPermission, ...swapOptions];
  }

  optionsRef.current = swapOptions;

  const selectedOptionIndex = Object.values(swapOptions).findIndex((chartOption) => {
    if (isDonutPie) {
      return chartOption.value === ChartType.DonutPie;
    }

    if (isHalfDonutPie) {
      return chartOption.value === ChartType.HalfDonutPie;
    }

    return chartOption.widgetType === widgetType;
  });

  return (
    <>
      <DropdownPopover
        options={generateTranslatedOptions(t, swapOptions)}
        selectedIndex={selectedOptionIndex}
        label={label}
        w={'200px'}
        spacing={1}
        buttonVariant={'toolbar-dropdown-option'}
        optionsButtonVariant={'toolbar-dropdown-item-basic'}
        onSelect={onSelect}
        triggerSelectOnSameValue={false}
        triggerProps={{
          height: 8,
          'data-testid': 'chart-swap-trigger',
        }}
        ariaLabel={ariaLabel}
        buttonOptionProps={{
          height: 7,
          justifyContent: 'flex-start',
        }}
        useLabelInsteadOfSelectedLabel
      />
    </>
  );
};

export default ChartSwapper;
